import { FingerprintJsProAgent } from "@fingerprintjs/fingerprintjs-pro-react-native";
import { Platform } from "react-native";
import * as Sentry from "@sentry/react-native";
import {
  getBrand,
  getBuildNumber,
  getCarrier,
  getDeviceId,
  getIpAddress,
  getModel,
  getSystemVersion,
  isEmulator,
} from "react-native-device-info";
import { fingerprintApiKey } from "@config";
import { get, set, remove } from "@lib/storage";

// Device identification for fraud blocking. We never send visitorId — the
// backend resolves it from the requestId via the Fingerprint Server API
// (zero-trust), so unlike cm-customer-app there is nothing the phone can edit.
//
// Every identification is billed, so one requestId is cached per phone number
// (MMKV) and reused while it is younger than the reuse window the backend
// serves as `fp_reuse_ttl_ms` on POST /cpdb/get-otp-db and
// POST /cpdb/db-onboarding/send-otp. The backend accepts an event up to that
// age only when Fingerprint linked it to the same phone, so a cached ID is
// useless for any other number. With no value stored (backend without the
// feature, or rolled back) the cache falls back to the backend's 5-minute
// rule, which is short enough that reuse is effectively off.

const CACHE_KEY = "fp_request_cache";
const TTL_KEY = "fp_reuse_ttl_ms";
// Today's backend limit for events not covered by the reuse window.
const FALLBACK_TTL_MS = 5 * 60 * 1000;
// Covers request time plus the gap between Fingerprint's event timestamp and
// our server clock, so a reused ID never arrives just past the window.
const SAFETY_MARGIN_MS = 60 * 1000;

interface CachedFpRequest {
  requestId: string;
  phone: string;
  savedAt: number;
}

/**
 * Store the reuse window served by the backend. Called after every send-OTP
 * response; `undefined` (older backend, 4xx resolved by the interceptor,
 * rollback) clears the stored value so the 5-min fallback applies.
 */
export const saveFpReuseTtl = (ttlMs: unknown): void => {
  if (typeof ttlMs === "number" && Number.isFinite(ttlMs) && ttlMs > 0) {
    set(TTL_KEY, String(ttlMs));
  } else {
    remove(TTL_KEY);
  }
};

const getReuseTtlMs = (): number => {
  const stored = Number(get(TTL_KEY));
  return Number.isFinite(stored) && stored > 0 ? stored : FALLBACK_TTL_MS;
};

const readCachedRequestId = (phone: string): string | null => {
  const raw = get(CACHE_KEY);
  if (!raw) return null;
  try {
    const cached = JSON.parse(raw) as Partial<CachedFpRequest>;
    if (!cached.requestId || cached.phone !== phone) return null;
    const ageMs = Date.now() - Number(cached.savedAt);
    if (!Number.isFinite(ageMs) || ageMs < 0) return null;
    if (ageMs >= getReuseTtlMs() - SAFETY_MARGIN_MS) return null;
    return cached.requestId;
  } catch {
    return null;
  }
};

const cacheRequestId = (requestId: string, phone: string): void => {
  const entry: CachedFpRequest = { requestId, phone, savedAt: Date.now() };
  set(CACHE_KEY, JSON.stringify(entry));
};

let agent: FingerprintJsProAgent | null = null;

const getAgent = (): FingerprintJsProAgent | null => {
  if (!fingerprintApiKey) return null;
  if (!agent) {
    agent = new FingerprintJsProAgent({
      apiKey: fingerprintApiKey,
      region: "ap",
    });
  }
  return agent;
};

/**
 * fp_request_id for a gated backend call — the cached one for this phone
 * number when still inside the reuse window, otherwise a fresh (billed)
 * identification. linkedId = phone number so the backend can detect replayed
 * requestIds minted for a different login, and Fingerprint's velocity signals
 * count distinct identities per device. `tag` is only stamped on a fresh
 * identification; a reused ID keeps the flow that minted it.
 *
 * Fail-safe: returns null on any error (no key, FP outage, web platform) —
 * the backend fails open and records the attempt with device_info only. An
 * SDK error leaves the cache untouched.
 */
export const getFpRequestId = async (
  phoneNumber: string,
  tag: string,
): Promise<string | null> => {
  if (Platform.OS === "web") return null;
  const fpAgent = getAgent();
  if (!fpAgent) return null;

  const cachedId = readCachedRequestId(phoneNumber);
  if (cachedId) return cachedId;

  try {
    const visitorData = await fpAgent.getVisitorData(
      { flow: tag },
      phoneNumber,
    );
    const requestId = visitorData?.requestId ?? null;
    if (requestId) cacheRequestId(requestId, phoneNumber);
    return requestId;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

/**
 * Client-reported device snapshot sent alongside fp_request_id. This is the
 * fail-open fallback trail — untrusted, but better than nothing when the
 * fingerprint can't be verified.
 */
export const getGateDeviceInfo = async (): Promise<string> => {
  try {
    const [carrier, ip, emulator] = await Promise.all([
      getCarrier().catch(() => null),
      getIpAddress().catch(() => null),
      isEmulator().catch(() => null),
    ]);
    return JSON.stringify({
      brand: getBrand(),
      model: getModel(),
      device_id: getDeviceId(),
      os: Platform.OS,
      os_version: getSystemVersion(),
      app_version: getBuildNumber(),
      carrier,
      ip,
      is_emulator: emulator,
    });
  } catch {
    return JSON.stringify({ error: "DEVICE_INFO_UNAVAILABLE" });
  }
};
